import axios from 'axios';
import EventEmitter from 'events';

import localStorage from 'Browser/localStorage';
import logger from 'Log/logger';

import JsSIP from './JsSIPWrapper';
import Exceptions from './Exceptions';

import s from './strings';

const FETCH_CONFIG_TIMEOUT = 500;

export class AppController extends EventEmitter {
  constructor({
    localStorageKey = null,
    storedFields = null,

    alwaysListenOnly = false,
    enableSettings = true,
  }) {
    super();

    this.log = logger('WebCall:AppController');

    this._currentCallController = null;
    this._callControllers = {};

    this._appConfig = null;
    this._formDefaults = {};

    this.localStorageKey = localStorageKey;
    this.storedFields = storedFields;

    this._alwaysListenOnly = alwaysListenOnly;
    this._enableSettings = enableSettings;

    this._debugMode = false;

    this._connectionError = null;

    this._axiosInstance = axios.create({
      transformResponse: data => JSON.parse(data),
      timeout: FETCH_CONFIG_TIMEOUT,
    });

    window.addEventListener('beforeunload', e => {
      if (this.isConnected) {
        e.preventDefault();
        e.returnValue = s.Errors.ERR_WEBCALL_NAVIGATE_AWAY;
      }
    });

    window.addEventListener('pagehide', e => {
      Object.values(this._callControllers).forEach(callController => {
        if (callController.isConnected) {
          callController.disconnect();
        }
      });
    });
  }

  loadStoredParams() {
    const { localStorageKey } = this;

    if (!(localStorageKey && localStorage[localStorageKey]))
      return;

    let decoded;
    try {
      decoded = JSON.parse(localStorage[localStorageKey]);
    } catch (e) {
    }

    if (decoded && typeof decoded === 'object')
      this.storedFields.forEach(field => {
        if (typeof decoded[field] === 'string')
          this._formDefaults[field] = decoded[field];
      });
  }

  saveStoredParams(formData) {
    if (this.localStorageKey) {
      const fieldsJSON = {};

      this.storedFields.forEach(name => fieldsJSON[name] = formData[name]);

      localStorage[this.localStorageKey] = JSON.stringify(fieldsJSON);
    }

    return formData;
  }

  clearStoredParams() {
    if (this.localStorageKey) {
      delete localStorage[this.localStorageKey];
    }
  }

  clearLocalStorage() {
    Object.keys(localStorage).forEach(key => {
      if (key.indexOf('webCall') !== 0) {
        return;
      }

      this.log(`deleting localStorage key ${key}`);

      delete localStorage[key];
    });
  }

  openSettings() {
    if (!this._callControllers.main) {
      throw new Error('missing main callController');
    }

    this._currentCallController = this._callControllers.main;
    this._currentCallController.openSettings();
  }

  connect(connectParams, callType = 'main') {
    this.log('connect()');

    const callController = this._callControllers[callType];
    if (!callController) {
      throw new Error('unknown callType');
    }

    this._currentCallController = callController;
    this._connectionError = null;

    Promise.resolve()
      .then(() => this._connect(callType, callController, connectParams))
      .catch(err => this._setConnectionError(err))
      .then(() => {
        this.emit('update');
      });
  }

  connectForm(callType = 'main') {
    this.log('connectForm()');

    if (typeof this._getFormData !== 'function') {
      throw new Error('getFormData not registered');
    }
    const formData = this._getFormData();

    const callController = this._callControllers[callType];
    if (!callController) {
      throw new Error('unknown callType');
    }

    this._currentCallController = callController;
    this._connectionError = null;

    Promise.resolve()
      .then(() => this.saveStoredParams(formData))
      .then(() => this.processFormData(callType, formData))
      .then(connectParams => this._connect(callType, callController, connectParams))
      .catch(err => this._setConnectionError(err))
      .then(() => {
        this.emit('update');
      });
  }

  _connect(callType, callController, connectParams) {
    return Promise.resolve()
      .then(() => this._parseUris(connectParams))
      .then(() => this.processConnectParams(callType, connectParams))
      .then(() => this.fetchConfig(connectParams))
      .then(() => callController.setConnectParams(connectParams))
      .then(() => callController.connect(this._alwaysListenOnly));
  }

  processFormData(callType, connectData) {
    throw new Error('processFormData must be implemented');
  }

  _parseUris(connectParams) {
    const parsed = JsSIP.Utils.normalizeTarget(connectParams.toSipURI);
    if (!parsed) {
      throw new Exceptions.ConfigError('Invalid toSipURI');
    }
    connectParams.toSipURI = parsed;
  }

  processConnectParams(callType, connectParams) {
  }

  addCallController(callType, ctrl) {
    this._callControllers[callType] = ctrl;
    ctrl.on('error', err => this._setConnectionError(err));

    if (!this._currentCallController && callType === 'main') {
      this._currentCallController = ctrl;
    }
  }

  setAppConfig(appConfig) {
    this._appConfig = appConfig;
  }

  setFormDefaults(formDefaults) {
    this._formDefaults = formDefaults;
  }

  registerGetFormData(getFormData) {
    this._getFormData = getFormData;
  }

  setDebugMode(debugMode) {
    this._debugMode = debugMode;
    Object.values(this._callControllers).forEach(ctrl => ctrl.setDebugMode(debugMode));
  }

  get currentCallController() {
    return this._currentCallController;
  }

  get isActive() {
    return Object.values(this._callControllers).some(ctrl => ctrl.isActive);
  }

  get isConnected() {
    return Object.values(this._callControllers).some(ctrl => ctrl.isConnected);
  }

  get formDefaults() {
    return this._formDefaults;
  }

  get alwaysListenOnly() {
    return this._alwaysListenOnly;
  }

  get enableSettings() {
    return this._enableSettings;
  }

  get debugMode() {
    return this._debugMode;
  }

  fetchConfig(connectParams) {
    return Promise.resolve()
      .then(() => this._fetchLocation(connectParams))
      .then(() => this._fetchRTCConfiguration(connectParams));
  }

  _fetchLocation(connectParams) {
    const { fetchLocationURL } = connectParams;

    if (!fetchLocationURL) {
      return null;
    }

    this.log(`_fetchLocation() | ${fetchLocationURL}`);

    return this._axiosInstance(fetchLocationURL)
      .then(res => {
        this.log('_fetchLocation() | success', res.data);
        return res && res.data && res.data.RegionCode || null;
      })
      .then(location => {
        if (location) {
          connectParams.toSipURI.setParam('location', location);
        }
      })
      .catch(err => {
        this.log('_fetchLocation() | failure', err);

        // swallow errors
        return null;
      });
  }

  _fetchRTCConfiguration(connectParams) {
    const { fetchRTCConfigurationURL } = connectParams;

    if (!fetchRTCConfigurationURL) {
      return null;
    }

    return Promise.resolve()
      .then(() => {
        const url = new URL(fetchRTCConfigurationURL);
        url.searchParams.set('fromSipURI', connectParams.fromSipURI);
        url.searchParams.set('toSipURI', connectParams.toSipURI);

        this.log(`_fetchRTCConfiguration() | ${url}`);

        return url;
      })
      .then(url => this._axiosInstance(url.toString()))
      .then(res => {
        this.log('_fetchRTCConfiguration() | success', res.data);
        return res && res.data || null;
      })
      .then(rtcConfiguration => {
        if (rtcConfiguration) {
          connectParams.rtcConfiguration = rtcConfiguration;
        }
      })
      .catch(err => {
        this.log('_fetchRTCConfiguration() | failure', err);

        // swallow errors
        return null;
      });
  }

  get listenOnlyDisabled() {
    if (!this._currentCallController)
      return false;

    return this._currentCallController.listenOnlyDisabled;
  }

  get connectionErrorMessage() {
    if (!this._connectionError)
      return null;

    return this._connectionError.message;
  }

  get connectionErrorType() {
    if (!this._connectionError)
      return null;

    return this._connectionError.name;
  }

  connectionErrorRetry(listenOnly = false) {
    this.log(`connectionErrorRetry() | listenOnly=${listenOnly}`);

    Promise.resolve()
      .then(() => this._currentCallController.connect(listenOnly))
      .then(() => {
        this._connectionError = null;
      })
      .catch(err => this._setConnectionError(err))
      .then(() => {
        this.emit('update');
      });
  }

  connectionErrorDismiss() {
    this._connectionError = null;
    this.emit('update');
  }

  _setConnectionError(err) {
    this.log('_setConnectionError()', err);
    if (err.cause)
      this.log('_setConnectionError() | cause', err.cause);

    this._connectionError = err;
  }
}
